import React, { useState, useEffect } from 'react';
import { warehouseService } from '../../../../services/warehouseService';
import { Warehouse } from '../../../../types/warehouse';

export const WarehouseAPITest: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [testResults, setTestResults] = useState<string[]>([]);

  const addTestResult = (message: string) => {
    setTestResults(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  const testAPI = async () => {
    setLoading(true);
    setError('');
    setWarehouses([]);
    setTestResults([]);

    try {
      addTestResult('🔄 Starting warehouse API test...');
      
      // Test environment variables
      const apiUrl = process.env.REACT_APP_API_URL;
      addTestResult(`📍 API Base URL: ${apiUrl || 'NOT SET'}`);
      
      // Test authentication token
      const token = localStorage.getItem('token');
      addTestResult(`🔑 Auth token: ${token ? `Present (${token.length} chars)` : 'MISSING'}`);
      
      // Test direct API call
      addTestResult('🚀 Making direct warehouse API call...');
      const fetchedWarehouses = await warehouseService.getWarehouses();
      
      addTestResult(`✅ API call successful! Found ${fetchedWarehouses.length} warehouses`);
      setWarehouses(fetchedWarehouses);
      
      // Test options conversion
      const options = warehouseService.convertToOptions(fetchedWarehouses);
      addTestResult(`✅ Converted to ${options.length} options`);
      addTestResult(`Options: ${JSON.stringify(options, null, 2)}`);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      addTestResult(`❌ Error: ${errorMessage}`);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const testDirectFetch = async () => {
    addTestResult('🔧 Testing direct fetch to warehouse API...');
    
    const apiUrl = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';
    const url = `${apiUrl}/warehouse/current`;
    const token = localStorage.getItem('token');
    
    try {
      addTestResult(`📡 Direct fetch to: ${url}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      addTestResult(`📡 Response status: ${response.status} ${response.statusText}`);
      addTestResult(`📡 Response ok: ${response.ok}`);
      
      if (response.ok) {
        const data = await response.json();
        addTestResult(`✅ Raw response: ${JSON.stringify(data, null, 2)}`);
      } else {
        const errorText = await response.text();
        addTestResult(`❌ Error response: ${errorText}`);
      }
      
    } catch (err) {
      addTestResult(`❌ Fetch error: ${err instanceof Error ? err.message : err}`);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold mb-4">Warehouse API Test</h1>
        
        <div className="space-y-4">
          <div className="flex space-x-2">
            <button
              onClick={testAPI}
              disabled={loading}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? 'Testing...' : 'Test Warehouse Service'}
            </button>
            
            <button
              onClick={testDirectFetch}
              className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
            >
              Test Direct Fetch
            </button>
          </div>

          {error && (
            <div className="bg-red-50 text-red-800 p-3 rounded border border-red-200">
              <strong>Error:</strong> {error}
            </div>
          )}

          {testResults.length > 0 && (
            <div className="bg-gray-50 p-4 rounded">
              <h3 className="font-semibold mb-2">Test Results:</h3>
              <pre className="text-sm overflow-x-auto whitespace-pre-wrap">
                {testResults.join('\n')}
              </pre>
            </div>
          )}

          {warehouses.length > 0 && (
            <div className="bg-green-50 p-4 rounded">
              <h3 className="font-semibold mb-2">Found Warehouses:</h3>
              <div className="grid gap-2">
                {warehouses.map(warehouse => (
                  <div key={warehouse.id} className="bg-white p-2 rounded border">
                    <div className="font-mono text-sm">
                      <strong>ID:</strong> {warehouse.warehouseID} | 
                      <strong> Description:</strong> {warehouse.warehouseDescr} |
                      <strong> Company:</strong> {warehouse.companyNo}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
